import { Box, Button, Typography, Grid, Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { ProductDetailHeading, ImageBox, DetailBox } from './style';

import EnrollBundleButton from '@components/molecules/enrollBundleButton';
import { ProductDetailContainer } from '@components/molecules/productDetail/style';
import BreadCrumbs from 'src/components/atoms/breadcrumb';
import { FavoriteIconButton, ShareIconButton } from 'src/components/atoms/iconButton';
import Image from 'src/components/atoms/image';
import NextLink from 'src/components/atoms/link';
import config from 'src/config';
import { EnrollmentStatus } from 'src/helpers/constants';
import { useMediaQueryDevice } from 'src/helpers/use';
import { numberWithCommas } from 'src/helpers/utils';
import { IBreadcrumb } from 'src/interfaces';

type Props = {
  originalPrice: number;
  salePrice: number;
  breadcrumbList: IBreadcrumb[];
  highlight_medium_image: string;
  name: string;
  editButton?: JSX.Element;
  cpdEnrollmentStatus: EnrollmentStatus;
  courseAmount?: number;
  isLoading?: boolean;
  onChooseEnrollClick: () => void;
};

const Heading = ({
  originalPrice,
  salePrice,
  breadcrumbList,
  highlight_medium_image,
  name,
  editButton,
  cpdEnrollmentStatus,
  courseAmount = 0,
  isLoading,
  onChooseEnrollClick,
}: Props & {}) => {
  const { t } = useTranslation();

  const breadcrumbs = breadcrumbList.map((item, index) => {
    if (!item.description && !item.url) return <></>;
    if (!item.url) {
      return (
        <Typography variant="h7" key={`cpd_course_detail_${index}`} className="text-discount">
          {item.description}
        </Typography>
      );
    }
    return (
      <NextLink key={`cpd_course_detail_${index}`} href={item.url}>
        <Button className="breadcrumb-link" variant="text" sx={{ color: 'white' }}>
          {item.description}
        </Button>
      </NextLink>
    );
  });

  return (
    <ProductDetailHeading>
      <ProductDetailContainer>
        <Grid container sx={{ m: { xs: '0 0 16px 0' }, width: { xs: '100%', md: '100%', lg: '100%' } }}>
          <Grid xs={12} md={8} item>
            <BreadCrumbs breadcrumbs={breadcrumbs} widthLastList="60%" color="white" />
          </Grid>
          <Grid md={4} item sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
              {editButton}
            </Box>
          </Grid>
        </Grid>

        <Grid container sx={{ m: { lg: '2.5rem 0', xs: '0 auto' }, width: { xs: '100%', md: '83.5%', lg: '100%' } }}>
          <Grid xs={12} lg={6} item>
            <ImageBox>
              <Image
                src={highlight_medium_image}
                errorImage={`${config.IMAGE_CDN_HOST}/assets/thumbnail-course-card.jpg`}
                alt={name}
              />
            </ImageBox>
          </Grid>
          <Grid xs={12} lg={6} item>
            <DetailBox>
              <Box>
                <Grid container alignItems="baseline">
                  <Grid xs={8} item>
                    <Typography variant="h7" className="text-discount">
                      {t('product_detail.bundle.typeOverline.courses', {
                        courseAmount,
                      })}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid md={10} lg={12} item>
                    <Typography
                      sx={{ typography: { xs: 'h4', md: 'h2' }, lineHeight: 1, mt: { md: 0.5, lg: 0 } }}
                      variant="h1"
                      color="white"
                      className="ellipsis-2"
                    >
                      {name}
                    </Typography>
                  </Grid>
                </Grid>
                {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                  <Typography
                    component="span"
                    sx={{ fontSize: { md: '48px', xs: '30px' }, fontWeight: 'bold', lineHeight: 1 }}
                    color="white"
                  >
                    {numberWithCommas(salePrice)} {t('home.course_card_baht')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="span"
                      sx={{ fontSize: { md: '48px', xs: '30px' }, fontWeight: 'bold', lineHeight: 1 }}
                      color="white"
                    >
                      {numberWithCommas(salePrice)} {t('home.course_card_baht')}
                    </Typography>
                    <Typography
                      className="strike-center text-discount"
                      sx={{
                        mx: { md: '1.5rem', xs: '1rem' },
                        typography: { md: 'h4', xs: 'h7' },
                        position: 'relative',
                        bottom: { md: '4px', xs: '1px' },
                      }}
                      component="span"
                    >
                      {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                    </Typography>
                  </>
                )}
              </Box>
              <Stack
                sx={{
                  height: '100%',
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'end',
                }}
              >
                <Grid className="train" container>
                  <Grid md={12} lg={9} width="100%" item>
                    <EnrollBundleButton
                      isLoading={isLoading}
                      type={cpdEnrollmentStatus}
                      onClick={onChooseEnrollClick}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </DetailBox>
          </Grid>
        </Grid>
      </ProductDetailContainer>
    </ProductDetailHeading>
  );
};

export default Heading;
